import { useEffect, useRef, useState } from 'react';

import {
  amountToCents,
  type CentsById,
  centsToAmount,
  interpolateCents,
} from '@/helpers/jackpot';

import { JACKPOT_POLL_INTERVAL } from './useCurrentJackpotCampaign';
import { useExchangeAmount } from './useExchangeAmount';
import { useJackpotPools } from './useJackpotPools';

const FRAME_INTERVAL = 100;

export const useAnimatedPools = () => {
  const { pools, isLoading } = useJackpotPools();
  const { getExchangeAmount, formatAmount } = useExchangeAmount();
  const [shownCents, setShownCents] = useState<CentsById>({});
  const shownCentsRef = useRef<CentsById>({});

  useEffect(() => {
    const startCents = shownCentsRef.current;
    const targetCents: CentsById = Object.fromEntries(
      pools.map(({ id, prize }) => [id, amountToCents(prize)])
    );

    const commit = (nextCents: CentsById) => {
      shownCentsRef.current = nextCents;
      setShownCents((prev) =>
        pools.every(({ id }) => prev[id] === nextCents[id]) ? prev : nextCents
      );
    };

    const isGrowing = pools.some(
      ({ id }) => targetCents[id] > (startCents[id] ?? Number.POSITIVE_INFINITY)
    );

    if (!isGrowing) {
      commit(targetCents);
      return;
    }

    const startedAt = performance.now();
    let committedAt = Number.NEGATIVE_INFINITY;
    let frameId = 0;

    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / JACKPOT_POLL_INTERVAL, 1);

      if (progress === 1 || now - committedAt >= FRAME_INTERVAL) {
        committedAt = now;
        commit(interpolateCents(startCents, targetCents, progress));
      }

      if (progress < 1) frameId = requestAnimationFrame(tick);
    };

    tick(startedAt);

    return () => cancelAnimationFrame(frameId);
  }, [pools]);

  const animatedPools = pools.map((pool) =>
    pool.id in shownCents
      ? { ...pool, prize: centsToAmount(shownCents[pool.id]) }
      : pool
  );

  const total = animatedPools.reduce(
    (sum, { prize }) => sum + getExchangeAmount(prize),
    0
  );

  return {
    pools: animatedPools,
    isLoading,
    formattedTotal: formatAmount(total, 2),
  };
};
